import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ArrowRight, Shield, Brain, Cloud, Database,
  Cpu, Globe, Users, Clock, DollarSign, CheckCircle, ExternalLink
} from 'lucide-react';

/* ── In-page sticky nav ─────────────────────────────────────────────── */
const sections = [
  { id: 'context',   label: 'Context' },
  { id: 'partners',  label: 'Partners' },
  { id: 'challenge', label: 'The Challenge' },
  { id: 'technologies', label: 'Technologies' },
  { id: 'solution',  label: 'Solution Overview' },
  { id: 'role',      label: "RakFort's Role" },
  { id: 'uses',      label: 'Uses' },
];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ── FAQ accordion ──────────────────────────────────────────────────── */
const faqs = [
  {
    q: 'What is the REINFORCE project?',
    a: 'REINFORCE is a Digital Europe Programme project developing an AI-powered cybersecurity ecosystem specifically designed for hospitals and healthcare infrastructure across Europe. It focuses on threat detection, compliance automation, and resilience planning.'
  },
  {
    q: 'Who funds REINFORCE?',
    a: 'The project is funded under the Digital Europe Programme (Project ID: 101299739) with a total budget of €9 million. It runs for 36 months and involves partners from 11 European countries.'
  },
  {
    q: 'How does REINFORCE address NIS2 and GDPR compliance?',
    a: 'REINFORCE embeds compliance checks directly into its AI monitoring layer, continuously auditing system behaviour against NIS2, GDPR, and EHDS requirements and generating audit-ready reports automatically.'
  },
  {
    q: "What is RakFort's role in REINFORCE?",
    a: 'RakFort serves as technical lead for the AI security and model robustness workpackages, contributing adversarial testing frameworks, LLM red-teaming pipelines, and risk governance tooling.'
  },
  {
    q: 'Can my healthcare organisation join the REINFORCE pilot?',
    a: 'Yes. The project is actively recruiting hospital groups and healthcare IT providers for its validation pilots across the EU. Contact us to learn more about participation criteria.'
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        className="w-full flex items-center justify-between py-5 text-left gap-4"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-[#2c2c2c] font-semibold text-[0.95rem]">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-[#005a9c] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="pb-5 text-[#5c5c5c] text-[0.9rem] leading-relaxed -mt-1">{a}</p>
      )}
    </div>
  );
}

/* ── Metric card ────────────────────────────────────────────────────── */
function MetricCard({ icon: Icon, value, label }) {
  return (
    <div className="bg-white/70 border border-[#e5c99a] rounded-xl p-5 flex flex-col items-center text-center gap-2">
      <Icon className="w-6 h-6 text-[#b45309]" />
      <span className="text-2xl font-bold text-[#1c1c1c]">{value}</span>
      <span className="text-xs text-[#78350f] font-medium">{label}</span>
    </div>
  );
}

/* ── Tech card ──────────────────────────────────────────────────────── */
function TechCard({ icon: Icon, name, desc }) {
  return (
    <div className="rf-card bg-white p-6 flex flex-col gap-4 hover:border-[#005a9c]/30 transition-all group">
      <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:bg-[#005a9c] group-hover:text-white transition-all">
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h4 className="font-bold text-[#2c2c2c] mb-1">{name}</h4>
        <p className="text-xs text-[#5c5c5c] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────── */
export default function ReinforceProject() {
  return (
    <div className="overflow-x-hidden">

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <div className="bg-[#fff7ed] border-b border-[#fed7aa]">
        <div className="max-w-[1140px] mx-auto px-5 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-center gap-12">
            <div className="flex-1 space-y-6">
              <Link
                to="/research"
                className="inline-flex items-center gap-1 text-xs text-[#b45309] font-semibold hover:underline"
              >
                ← Research / Our Projects
              </Link>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#b45309]/10 border border-[#b45309]/20 text-[#b45309] text-xs font-bold tracking-wider uppercase">
                Cybersecurity
              </div>
              <h1 className="text-3xl md:text-[2.75rem] font-bold text-[#1c1c1c] leading-[1.12] tracking-tight">
                AI-Driven Cybersecurity for Hospitals and Healthcare Infrastructure
              </h1>
              <div className="flex items-center gap-3">
                <img
                  src={require('../img/rakfort.jpg')}
                  alt="RakFort"
                  className="h-8 w-8 rounded-sm"
                />
                <span className="text-sm font-semibold text-[#2c2c2c]">REINFORCE</span>
                <span className="text-slate-300">·</span>
                <span className="text-xs text-[#5c5c5c]">Project ID: 101299739</span>
              </div>
            </div>

            {/* decorative badge area */}
            <div className="shrink-0 w-full md:w-[340px] h-[220px] rounded-2xl bg-[#fed7aa]/50 border border-[#fbbf24]/30 flex items-center justify-center relative overflow-hidden">
              <Shield className="w-32 h-32 text-[#f59e0b] opacity-20 absolute -bottom-4 -right-4 rotate-12" />
              <div className="relative z-10 text-center space-y-3 p-6">
                <div className="w-16 h-16 rounded-2xl bg-white border border-[#fbbf24]/40 flex items-center justify-center mx-auto">
                  <Shield className="w-8 h-8 text-[#b45309]" />
                </div>
                <p className="text-[#78350f] font-bold text-lg">REINFORCE</p>
                <p className="text-xs text-[#92400e]">Digital Europe Programme</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── In-page sticky nav ────────────────────────────────────────── */}
      <div className="bg-white border-b border-[#e5eef6] sticky top-[56px] z-20 overflow-x-auto">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="flex gap-0 whitespace-nowrap">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className="px-5 py-3.5 text-sm font-medium text-[#5c5c5c] hover:text-[#005a9c] hover:bg-[#f0f7ff] border-b-2 border-transparent hover:border-[#005a9c] transition-all"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Context ───────────────────────────────────────────────────── */}
      <section id="context" className="py-16 md:py-24 bg-white">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c]">Context</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2c2c2c] leading-snug">
                A European initiative for resilient healthcare cybersecurity
              </h2>
              <p className="text-[#5c5c5c] leading-relaxed">
                REINFORCE is a <strong>Digital Europe Programme</strong> project (Project ID: 101299739)
                developing an AI-powered ecosystem that detects, analyses, and responds to cybersecurity
                threats targeting hospitals and critical health infrastructure across Europe.
              </p>
              <p className="text-[#5c5c5c] leading-relaxed">
                The project embeds compliance support for <strong>NIS2</strong>, <strong>GDPR</strong>,
                and <strong>EHDS</strong> directly into its monitoring and response platform, helping
                healthcare operators meet regulatory obligations without additional overhead.
              </p>

              {/* Services tags */}
              <div className="flex flex-wrap gap-2 pt-2">
                {[
                  'AI/ML & Data Engineering',
                  'Software Development',
                  'Cloud Architecture',
                  'Data Engineering & Big Data',
                  'Digital Twins',
                  'MLOps',
                ].map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] font-semibold px-3 py-1 rounded-full bg-[#f0f7ff] border border-[#c5d4e3] text-[#005a9c]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Metric cards */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <MetricCard icon={Clock} value="36 months" label="Project duration" />
                <MetricCard icon={DollarSign} value="€9M" label="Total budget" />
                <MetricCard icon={Globe} value="11 countries" label="EU partner nations" />
                <MetricCard icon={Users} value="15+ partners" label="Consortium size" />
              </div>
              <div className="bg-[#fff7ed] border border-[#fed7aa] rounded-xl p-5 text-sm text-[#78350f] font-medium">
                Funded under the <strong>Digital Europe Programme</strong> — supporting digital transformation
                of critical sectors across the European Union.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Partners ──────────────────────────────────────────────────── */}
      <section id="partners" className="py-16 md:py-24 bg-[#f8fbfd] border-y border-[#e5eef6]">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="text-center mb-12 space-y-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c]">Partners</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2c2c2c]">
              A pan-European consortium
            </h2>
            <p className="text-[#5c5c5c] max-w-xl mx-auto text-sm leading-relaxed">
              REINFORCE brings together technology companies, research institutions, and healthcare
              operators from 11 European countries.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { name: 'RakFort', country: 'Ireland' },
              { name: 'NOVAOPTIC', country: 'Romania' },
              { name: 'InQbit Innovations', country: 'Greece' },
              { name: 'DPG Digital Media', country: 'Cyprus' },
              { name: 'CyberMed GmbH', country: 'Germany' },
              { name: 'HealthGrid SRL', country: 'Italy' },
              { name: 'SecureNord AB', country: 'Sweden' },
              { name: 'HelsinAI Oy', country: 'Finland' },
              { name: 'DataLux SA', country: 'Luxembourg' },
              { name: 'IberiaSec SL', country: 'Spain' },
              { name: 'AtlantisRisk AS', country: 'Norway' },
            ].map((p) => (
              <div
                key={p.name}
                className="rf-card bg-white p-4 flex flex-col items-center text-center gap-2 hover:border-[#005a9c]/30 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#f0f7ff] border border-[#c5d4e3] flex items-center justify-center">
                  <Globe className="w-5 h-5 text-[#005a9c]" />
                </div>
                <span className="text-xs font-bold text-[#2c2c2c] leading-tight">{p.name}</span>
                <span className="text-[10px] text-[#5c5c5c]">{p.country}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Challenge ─────────────────────────────────────────────── */}
      <section id="challenge" className="py-16 md:py-24 bg-[#005a9c] text-white overflow-hidden relative">
        {/* decorative */}
        <div className="absolute top-6 right-12 opacity-10">
          <Shield className="w-64 h-64" />
        </div>
        <div className="absolute bottom-6 left-6 opacity-5">
          <Database className="w-48 h-48" />
        </div>

        <div className="max-w-[1140px] mx-auto px-5 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* image placeholder */}
            <div className="rounded-2xl bg-white/10 border border-white/20 h-[300px] flex items-center justify-center relative overflow-hidden">
              <Shield className="w-24 h-24 text-white/30" />
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
            </div>

            <div className="space-y-6">
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-200">The Challenge</span>
              <h2 className="text-2xl md:text-3xl font-bold leading-snug">
                Healthcare is the most targeted sector for ransomware and data theft
              </h2>
              <p className="text-blue-100 leading-relaxed">
                Hospitals operate life-critical systems with highly sensitive patient data, yet many run
                legacy infrastructure unable to detect or respond to modern AI-assisted attacks. A single
                breach can halt clinical operations, compromise patient safety, and violate GDPR obligations
                simultaneously.
              </p>
              <p className="text-blue-100 leading-relaxed">
                Compliance complexity — spanning NIS2, GDPR, and the emerging EHDS — places enormous
                administrative burden on already stretched IT and clinical teams.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-2">
                {[
                  'Ransomware escalation',
                  'Legacy system exposure',
                  'Regulatory complexity',
                  'Staff security awareness',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-300 shrink-0" />
                    <span className="text-sm text-blue-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Technologies ──────────────────────────────────────────────── */}
      <section id="technologies" className="py-16 md:py-24 bg-white">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="mb-10 space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c]">Technologies</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2c2c2c]">Cutting-edge technology stack</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            <TechCard icon={Brain} name="Mistral AI / LLMs" desc="Open-weight large language models for anomaly reasoning and alert triage." />
            <TechCard icon={Shield} name="Adversarial ML" desc="Red-teaming and robustness testing pipelines for production AI models." />
            <TechCard icon={Cloud} name="Cloud Architecture" desc="Multi-cloud deployment with zero-trust networking and SIEM integration." />
            <TechCard icon={Database} name="Data Engineering" desc="Real-time streaming pipelines for clinical log ingestion and SIEM correlation." />
            <TechCard icon={Cpu} name="Digital Twins" desc="Virtual replicas of hospital network topologies for safe attack simulation." />
            <TechCard icon={Globe} name="MLOps" desc="Automated model retraining and drift detection for evolving threat landscapes." />
          </div>
        </div>
      </section>

      {/* ── Solution Overview ─────────────────────────────────────────── */}
      <section id="solution" className="py-16 md:py-24 bg-[#f0f7ff] border-y border-[#e5eef6]">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div className="space-y-5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c]">Solution Overview</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2c2c2c] leading-snug">
                An AI-native cybersecurity platform built for healthcare
              </h2>
              <p className="text-[#5c5c5c] leading-relaxed">
                The REINFORCE platform combines real-time threat detection, automated compliance
                reporting, and digital-twin-based attack simulation into a unified dashboard tailored
                for clinical and IT teams alike.
              </p>
              <p className="text-[#5c5c5c] leading-relaxed">
                The system continuously learns from federated threat intelligence shared across the
                consortium, allowing each hospital to benefit from network-wide threat knowledge without
                exposing sensitive internal data.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { title: 'Real-time threat detection', desc: 'ML-based anomaly detection across network, endpoint, and clinical device logs.' },
                { title: 'Automated compliance reporting', desc: 'Audit-ready reports generated automatically against NIS2, GDPR, and EHDS.' },
                { title: 'Federated threat intelligence', desc: 'Privacy-preserving knowledge sharing across the REINFORCE partner network.' },
                { title: 'Digital twin simulation', desc: 'Safe red-team exercises on virtual hospital replicas without operational risk.' },
              ].map((item) => (
                <div key={item.title} className="rf-card bg-white p-6 hover:border-[#005a9c]/30 transition-all">
                  <h4 className="font-bold text-[#2c2c2c] mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#005a9c] shrink-0" />
                    {item.title}
                  </h4>
                  <p className="text-sm text-[#5c5c5c] leading-relaxed pl-6">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── RakFort's Role ────────────────────────────────────────────── */}
      <section id="role" className="py-16 md:py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 opacity-5 translate-x-1/4 -translate-y-1/4">
          <Globe className="w-96 h-96" />
        </div>
        <div className="max-w-[1140px] mx-auto px-5 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">RakFort's Role</span>
              <h2 className="text-2xl md:text-3xl font-bold leading-snug">
                Technical lead for AI security and model robustness
              </h2>
              <p className="text-slate-400 leading-relaxed">
                RakFort leads the AI security workpackage within REINFORCE, bringing adversarial ML
                expertise, LLM red-teaming infrastructure, and risk governance tooling to the consortium.
              </p>
              <p className="text-slate-400 leading-relaxed">
                Our team is responsible for designing the federated threat intelligence layer and the
                digital twin simulation environment — ensuring the platform can evolve alongside the
                threat landscape.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Brain, label: 'AI Security Lead' },
                { icon: Shield, label: 'Adversarial Testing' },
                { icon: Database, label: 'Federated Intelligence' },
                { icon: Cpu, label: 'Digital Twin R&D' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-5 flex flex-col items-center text-center gap-3">
                  <Icon className="w-8 h-8 text-blue-300" />
                  <span className="text-sm font-semibold text-slate-200">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Uses / Target Groups ──────────────────────────────────────── */}
      <section id="uses" className="py-16 md:py-24 bg-white">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="text-center mb-10 space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c]">Uses</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2c2c2c]">Who benefits from REINFORCE</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Hospital Groups',
                desc: 'Gain real-time threat visibility, automated NIS2/GDPR compliance, and staff security training tools integrated into clinical workflows.'
              },
              {
                icon: Globe,
                title: 'National Health Authorities',
                desc: 'Access cross-border threat intelligence sharing and policy-grade reporting dashboards for sector-wide situational awareness.'
              },
              {
                icon: Shield,
                title: 'Healthcare IT Providers',
                desc: 'Integrate REINFORCE APIs and SDKs into existing HIS/EHR platforms to add AI-native security without rebuilding core infrastructure.'
              },
            ].map((item) => (
              <div key={item.title} className="rf-card p-8 hover:border-[#005a9c]/30 transition-all group">
                <div className="w-12 h-12 rounded-xl bg-[#f0f7ff] border border-[#c5d4e3] flex items-center justify-center mb-6 group-hover:bg-[#005a9c] group-hover:text-white transition-all">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-[#2c2c2c] mb-3">{item.title}</h3>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <section className="py-16 md:py-24 bg-[#f8fbfd] border-y border-[#e5eef6]">
        <div className="max-w-[1140px] mx-auto px-5">
          <div className="grid md:grid-cols-3 gap-16">
            <div className="space-y-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c]">FAQ</span>
              <h2 className="text-2xl font-bold text-[#2c2c2c] leading-snug">
                Frequently asked questions
              </h2>
              <p className="text-sm text-[#5c5c5c] leading-relaxed">
                Have a question not answered here? Reach out to our research team.
              </p>
              <Link
                to="/contactus"
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#005a9c] hover:underline mt-2"
              >
                Contact us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="md:col-span-2 bg-white border border-[#e5eef6] rounded-2xl px-8 divide-y divide-slate-100">
              {faqs.map((faq) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="py-24 bg-[#005a9c] text-white">
        <div className="max-w-[1140px] mx-auto px-5 text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">Interested in REINFORCE?</h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you are a healthcare provider, research partner, or technology vendor,
            we would love to hear from you.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contactus"
              className="px-10 py-4 bg-white text-[#005a9c] font-bold rounded-lg hover:bg-slate-50 transition-all shadow-xl inline-block"
            >
              Get in touch
            </Link>
            <Link
              to="/research"
              className="px-10 py-4 bg-white/10 border border-white/30 text-white font-bold rounded-lg hover:bg-white/20 transition-all inline-flex items-center gap-2"
            >
              View all projects <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
